import { useState } from 'react';
import type { AiSettings } from '../types';
import { LANGUAGES } from '../types';

interface Props {
  form: AiSettings;
}

const SAMPLE_CONVO_WITH_LEAD = [
  {
    role: 'bot',
    text: (name: string) =>
      `Hi! I'm ${name || 'your studio assistant'} 👋 Before we chat, mind sharing your name and email?`,
  },
  { role: 'lead-form', text: () => '' },
  {
    role: 'bot',
    text: (name: string) =>
      `Thanks, Sarah! Great to meet you. I'm ${name || 'here'} to help. What brings you here today?`,
  },
  { role: 'user', text: () => "I'm looking for a photographer for my wedding next spring." },
  {
    role: 'bot',
    text: () =>
      `Congratulations on your upcoming wedding! 🥂 We'd love to capture that day for you. Could you share your wedding date and approximate guest count so I can suggest the best package?`,
  },
  { role: 'user', text: () => 'June 14th, about 150 guests.' },
  {
    role: 'bot',
    text: () =>
      `Perfect! June 14th is a beautiful time of year. Based on 150 guests, I'd recommend our Full Day Premium package — it includes 10 hours of coverage, 2 photographers, and 500+ edited photos. Would you like to schedule a free 15-minute consultation call?`,
  },
];

const SAMPLE_CONVO_NO_LEAD = [
  {
    role: 'bot',
    text: (name: string) =>
      `Hi! I'm ${name || 'your studio assistant'}. How can I help you today?`,
  },
  { role: 'user', text: () => "What's included in your wedding packages?" },
  {
    role: 'bot',
    text: () =>
      `Great question! Our wedding packages range from 6 to 12 hours of coverage. Every package includes fully edited high-res photos, an online gallery, and print release. Would you like details on a specific package?`,
  },
];

const TONE_GREETING: Record<string, string> = {
  professional: 'Good day! How may I assist you?',
  friendly: "Hey! So excited to chat with you — how can I help?",
  casual: "Hey there! What can I help you with?",
  elegant: 'Welcome. It is a pleasure to assist you today.',
  persuasive: "Hi! You're one step away from booking the perfect photographer — let me help!",
  empathetic: "Hi there 😊 I'm here for you — what's on your mind?",
};

export default function PreviewTab({ form }: Props) {
  const [simulating, setSimulating] = useState(false);
  const [showFull, setShowFull] = useState(false);

  const convo = form.lead_capture_enabled
    ? SAMPLE_CONVO_WITH_LEAD
    : SAMPLE_CONVO_NO_LEAD;

  const langLabel =
    LANGUAGES.find((l) => l.value === form.language)?.label ?? form.language;

  const completeness = [
    form.assistant_enabled,
    !!form.assistant_name.trim(),
    !!form.tone,
    !!form.language,
    form.business_context.length >= 50,
    form.services_context.length >= 30,
  ].filter(Boolean).length;

  const pct = Math.round((completeness / 6) * 100);

  return (
    <div className="space-y-5">

      {/* Config summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[#0f172a] font-semibold text-sm">Configuration Summary</h3>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-28 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${pct}%`,
                  backgroundColor: pct === 100 ? '#10b981' : '#C8A96E',
                }}
              />
            </div>
            <span
              className={`text-xs font-semibold ${
                pct === 100 ? 'text-emerald-500' : 'text-[#C8A96E]'
              }`}
            >
              {pct}%
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {[
            {
              label: 'Status',
              value: form.assistant_enabled ? 'Enabled' : 'Disabled',
              ok: form.assistant_enabled,
              icon: 'ri-toggle-line',
            },
            {
              label: 'Name',
              value: form.assistant_name || '—',
              ok: !!form.assistant_name.trim(),
              icon: 'ri-user-star-line',
            },
            {
              label: 'Language',
              value: langLabel,
              ok: true,
              icon: 'ri-translate-2',
            },
            {
              label: 'Tone',
              value: form.tone.charAt(0).toUpperCase() + form.tone.slice(1),
              ok: true,
              icon: 'ri-palette-line',
            },
            {
              label: 'Lead Capture',
              value: form.lead_capture_enabled ? 'On' : 'Off',
              ok: form.lead_capture_enabled,
              icon: 'ri-user-received-line',
            },
            {
              label: 'Business Context',
              value:
                form.business_context.length >= 50
                  ? `${form.business_context.length} chars`
                  : 'Incomplete',
              ok: form.business_context.length >= 50,
              icon: 'ri-building-line',
            },
            {
              label: 'Booking Goal',
              value: form.appointment_goal_enabled ? 'Active' : 'Off',
              ok: form.appointment_goal_enabled,
              icon: 'ri-calendar-check-line',
            },
            {
              label: 'WhatsApp Handoff',
              value: form.whatsapp_handoff_enabled ? 'Active' : 'Off',
              ok: form.whatsapp_handoff_enabled,
              icon: 'ri-whatsapp-line',
            },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-between gap-2 px-3 py-2.5 bg-slate-50 rounded-lg"
            >
              <div className="flex items-center gap-2">
                <i className={`${item.icon} text-xs text-slate-400`} />
                <span className="text-slate-500 text-xs">{item.label}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className={`text-xs font-medium ${
                    item.ok ? 'text-[#0f172a]' : 'text-slate-300'
                  }`}
                >
                  {item.value}
                </span>
                <div
                  className={`w-4 h-4 flex items-center justify-center rounded-full ${
                    item.ok ? 'bg-emerald-50' : 'bg-slate-100'
                  }`}
                >
                  <i
                    className={`text-[9px] ${
                      item.ok
                        ? 'ri-check-line text-emerald-500'
                        : 'ri-subtract-line text-slate-300'
                    }`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chat preview */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[#0f172a] font-semibold text-sm">Simulated Conversation</h3>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-xs">{langLabel}</span>
            <span className="w-1 h-1 rounded-full bg-slate-300" />
            <span className="text-[#C8A96E] text-xs capitalize">{form.tone}</span>
          </div>
        </div>

        {/* Chat widget mockup */}
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 flex items-center gap-2.5" style={{ backgroundColor: '#C8A96E' }}>
            <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/20">
              <i className="ri-robot-line text-white text-sm" />
            </div>
            <div>
              <p className="text-white text-xs font-semibold">
                {form.assistant_name || 'Sales Assistant'}
              </p>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300" />
                <span className="text-white/70 text-[10px]">Online</span>
              </div>
            </div>
          </div>

          {/* Tone greeting */}
          <div className="px-4 pt-4 pb-2 bg-slate-50">
            <div className="flex items-end gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-full shrink-0" style={{ backgroundColor: 'rgba(200,169,110,0.2)' }}>
                <i className="ri-robot-line text-[#C8A96E] text-[10px]" />
              </div>
              <div className="bg-white rounded-2xl rounded-bl-sm px-3 py-2 border border-slate-200 max-w-[80%]">
                <p className="text-[#0f172a] text-[11px] leading-relaxed">
                  {TONE_GREETING[form.tone] ?? TONE_GREETING.professional}
                </p>
              </div>
            </div>
          </div>

          {/* Simulated messages */}
          <div className="px-4 pb-4 bg-slate-50 space-y-2.5">
            {(showFull ? convo : convo.slice(0, 4)).map((msg, i) => {
              if (msg.role === 'lead-form') {
                if (!form.lead_capture_enabled) return null;
                return (
                  <div key={i} className="bg-white rounded-xl border border-[#C8A96E]/30 p-3">
                    <p className="text-slate-400 text-[10px] mb-2">
                      Lead capture form
                    </p>
                    <div className="space-y-1.5">
                      <div className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                        <p className="text-slate-300 text-[10px]">Name…</p>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                        <p className="text-slate-300 text-[10px]">Email…</p>
                      </div>
                      <div className="rounded-lg py-1.5 text-center text-[10px] font-semibold text-white" style={{ backgroundColor: '#C8A96E' }}>
                        Start chatting
                      </div>
                    </div>
                  </div>
                );
              }
              if (msg.role === 'bot') {
                return (
                  <div key={i} className="flex items-end gap-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full shrink-0" style={{ backgroundColor: 'rgba(200,169,110,0.2)' }}>
                      <i className="ri-robot-line text-[#C8A96E] text-[10px]" />
                    </div>
                    <div className="bg-white rounded-2xl rounded-bl-sm px-3 py-2 border border-slate-200 max-w-[80%]">
                      <p className="text-[#0f172a] text-[11px] leading-relaxed">
                        {msg.text(form.assistant_name)}
                      </p>
                    </div>
                  </div>
                );
              }
              return (
                <div key={i} className="flex items-end justify-end gap-2">
                  <div
                    className="rounded-2xl rounded-br-sm px-3 py-2 max-w-[80%]"
                    style={{ backgroundColor: '#C8A96E' }}
                  >
                    <p className="text-white text-[11px] leading-relaxed">{msg.text(form.assistant_name)}</p>
                  </div>
                </div>
              );
            })}

            {convo.length > 4 && (
              <button
                type="button"
                onClick={() => {
                  setSimulating(true);
                  setTimeout(() => { setShowFull(!showFull); setSimulating(false); }, 400);
                }}
                className="w-full text-center text-[#C8A96E] text-xs hover:text-[#b8965a] transition-colors cursor-pointer py-1"
              >
                {simulating ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <div className="w-3 h-3 border border-[#C8A96E] border-t-transparent rounded-full animate-spin" />
                    Loading…
                  </span>
                ) : showFull ? (
                  '▲ Show less'
                ) : (
                  '▼ Show full conversation'
                )}
              </button>
            )}
          </div>

          {/* Input mockup */}
          <div className="px-4 py-3 border-t border-slate-200 bg-white flex items-center gap-2">
            <input
              type="text"
              placeholder="Type a message…"
              readOnly
              className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 text-[11px] text-slate-300 outline-none cursor-default"
            />
            <button
              type="button"
              className="w-7 h-7 flex items-center justify-center rounded-full"
              style={{ backgroundColor: '#C8A96E' }}
            >
              <i className="ri-send-plane-fill text-white text-xs" />
            </button>
          </div>
        </div>

        <p className="text-slate-300 text-[10px] text-center mt-3">
          Sample preview — real responses depend on business context, tone and AI model.
        </p>
      </div>

      {/* Ready checklist */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-[#0f172a] font-semibold text-sm mb-4">Pre-launch Checklist</h3>
        <div className="space-y-2.5">
          {[
            { label: 'Assistant is enabled', done: form.assistant_enabled },
            { label: 'Assistant name is set', done: !!form.assistant_name.trim() },
            { label: 'Business context written (50+ chars)', done: form.business_context.length >= 50 },
            { label: 'Services context written (30+ chars)', done: form.services_context.length >= 30 },
            { label: 'Pricing context added', done: form.pricing_context.length > 0 },
            { label: 'FAQ context added', done: form.faq_context.length > 0 },
            { label: 'Lead capture configured', done: true },
            { label: 'Tone & language selected', done: !!form.tone && !!form.language },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2.5">
              <div
                className={`w-5 h-5 flex items-center justify-center rounded-full shrink-0 ${
                  item.done ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-200'
                }`}
              >
                <i
                  className={`text-[10px] ${
                    item.done ? 'ri-check-line text-emerald-500' : 'ri-subtract-line text-slate-300'
                  }`}
                />
              </div>
              <span className={`text-xs ${item.done ? 'text-[#0f172a]/70' : 'text-slate-400'}`}>
                {item.label}
              </span>
            </div>
          ))}
        </div>

        {pct === 100 && (
          <div className="mt-4 px-4 py-3 bg-emerald-50 rounded-xl border border-emerald-200 flex items-center gap-2.5">
            <i className="ri-rocket-line text-emerald-500 text-sm shrink-0" />
            <p className="text-emerald-700 text-xs font-medium">
              Your assistant is fully configured and ready to deploy!
            </p>
          </div>
        )}
      </div>
    </div>
  );
}